/**
 * Geometry / math utilities shared by every particle effect.
 *
 * Kept tiny so the per-effect renderers don't all duplicate the same
 * helpers (heart curve, easing, RGB hex→rgba conversion, etc.).
 */

export function random(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function randomStatic(seed: number): number {
  return Math.sin(seed * 127.1) * 0.5 + Math.sin(seed * 311.7) * 0.5;
}

export function softRandom(center: number, spread: number): number {
  return center + (Math.random() + Math.random() + Math.random() - 1.5) * spread;
}

export function choose<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function easeOutCubic(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
}

/** (x, y) coords at a given angle along a heart curve in [-1..1]. */
export function heartPoint(angle: number): { x: number; y: number } {
  const x = (16 * Math.pow(Math.sin(angle), 3)) / 18;
  const y =
    (13 * Math.cos(angle) -
      5 * Math.cos(2 * angle) -
      2 * Math.cos(3 * angle) -
      Math.cos(4 * angle)) /
    18;
  return { x, y };
}

/** Implicit-heart influence used by the `aurora-heart` effect. */
export function heartInfluence(nx: number, layer: number, time: number): number {
  const y = (layer - 0.52) * 2.4 + Math.sin(time * 0.35) * 0.08;
  const x = nx;
  const field = Math.pow(x * x + y * y - 1, 3) - x * x * y * y * y;
  return Math.max(0, 1 - Math.abs(field) * 4.5);
}

/** `#rrggbb` or `#rgb` → `rgba(r, g, b, a)`. */
export function rgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const normalized =
    value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const numeric = Number.parseInt(normalized, 16);
  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
